import React, { useState } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone, Mail, ArrowRight } from 'lucide-react';
import { companyInfo } from '../mock';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-lg fixed w-full top-0 z-50 border-b border-orange-100">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Enhanced */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full opacity-20"></div>
              <img 
                src={companyInfo.logoUrl} 
                alt="Phoenix Well Control Academy" 
                className="h-12 w-12 object-contain relative"
              />
            </div>
            <div>
              <h1 className="font-black text-xl bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
                PHOENIX WELLCONTROL
              </h1>
              <p className="text-xs text-gray-600 font-semibold tracking-wider">ACADEMY</p>
            </div>
          </div>

          {/* Desktop Navigation - Modern Style */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-orange-600 font-semibold transition-all hover:scale-105 relative group"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-orange-600 font-semibold transition-all hover:scale-105 relative group"
            >
              About
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
            </button>
            <button 
              onClick={() => scrollToSection('courses')}
              className="text-gray-700 hover:text-orange-600 font-semibold transition-all hover:scale-105 relative group"
            >
              Training Programs
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
            </button>
            <button 
              onClick={() => scrollToSection('gallery')}
              className="text-gray-700 hover:text-orange-600 font-semibold transition-all hover:scale-105 relative group"
            >
              Gallery
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
            </button>
            <button 
              onClick={() => scrollToSection('location')}
              className="text-gray-700 hover:text-orange-600 font-semibold transition-all hover:scale-105 relative group"
            >
              Location
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-orange-600 font-semibold transition-all hover:scale-105 relative group"
            >
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
            </button>
          </nav>

          {/* Contact Info & CTA - Enhanced */}
          <div className="hidden xl:flex items-center space-x-6">
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-2 text-gray-600 bg-gray-100 px-3 py-2 rounded-full">
                <Phone className="h-4 w-4" />
                <span className="font-medium">{companyInfo.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600 bg-blue-100 px-3 py-2 rounded-full">
                <Mail className="h-4 w-4" />
                <span className="font-medium">hello@phoenixwellcontrol.com</span>
              </div>
            </div>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
            >
              Get Certified
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Mobile Menu Button - Enhanced */}
          <button 
            className="lg:hidden p-2 rounded-xl bg-orange-100 hover:bg-orange-200 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? 
              <X className="h-6 w-6 text-orange-700" /> : 
              <Menu className="h-6 w-6 text-orange-700" />
            }
          </button>
        </div>

        {/* Mobile Menu - Enhanced */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-orange-100 rounded-b-2xl shadow-2xl">
            <nav className="py-6 space-y-4">
              <button 
                onClick={() => scrollToSection('home')}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 font-semibold transition-all"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 font-semibold transition-all"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('courses')}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 font-semibold transition-all"
              >
                Training Programs
              </button>
              <button 
                onClick={() => scrollToSection('gallery')}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 font-semibold transition-all"
              >
                Gallery
              </button>
              <button 
                onClick={() => scrollToSection('location')}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 font-semibold transition-all"
              >
                Location
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 font-semibold transition-all"
              >
                Contact
              </button>
              
              <div className="px-6 py-4 border-t border-orange-100 space-y-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span className="font-medium">{companyInfo.phone}</span>
                </div>
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold py-3 rounded-xl"
                >
                  Get Certified Today
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};